﻿import { FC, useCallback, useEffect, useState } from 'react';
import { Loading } from 'components/uikit/suspense/Loading';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';

import styles from './AddEditSignerModal.module.scss';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { Field } from 'components/uikit/Field.tsx';
import { Input } from 'components/uikit/inputs/Input.tsx';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm.ts';
import { PatchSignerDto, SignerDto } from 'services/api/api-client.types.ts';
import { QueryFactory } from 'services/api';
import { useQueryClient } from '@tanstack/react-query';
import { requiredRule } from 'helpers/form/react-hook-form-helper.ts';
import { FormError } from 'components/uikit/FormError.tsx';
import { useModal } from 'components/uikit/modal/useModal.tsx';
import { useSignatureBlockQueriesInvalidation } from 'pages/authorized/documents/documentList/signatureBlock/useSignatureBlockQueriesInvalidation';

type EditSignerModalProps = {
  signerId: number;
  onClose: () => void;
  isOpen: boolean;
  onUpdated?: (updateSigner: SignerDto) => void;
};

export const EditSignerModal: FC<EditSignerModalProps> = (props) => {
  const i18n = useScopedTranslation('Component.ManageSignerModal');
  const buttonTitle = useScopedTranslation('Button.Title');
  const queryClient = useQueryClient();
  const modal = useModal();
  const invalidateSignatureBlockQueries = useSignatureBlockQueriesInvalidation(
    {},
  );

  const [showOverallErrors, setShowOverallErrors] = useState(false);

  const signerQuery = QueryFactory.SignerQuery.useGetQuery(props.signerId!, {
    enabled: !!props.signerId,
  });

  useEffect(() => {
    if (signerQuery.data) {
      form.setValue('firstName', signerQuery.data.firstName);
      form.setValue('lastName', signerQuery.data.lastName);
      form.setValue('email', signerQuery.data.email);
    }
  }, [signerQuery.data]);

  const form = useAdvancedForm<PatchSignerDto>(
    useCallback(
      async (data) => {
        const projects = await QueryFactory.SignerClient.verifyPatchAbility(
          props.signerId!,
        );
        const confirm = await modal.showConfirm({
          title: i18n.t('title_edit'),
          text: i18n.t('edit_confirm_message', {
            name: `${signerQuery.data?.firstName} ${signerQuery.data?.lastName}`,
            projects: projects.map((p) => '«' + p + '»').join(', '),
          }),
        });

        if (!confirm) return;
        setShowOverallErrors(true);
        const updatedSigner = await QueryFactory.SignerClient.patch(
          props.signerId!,
          data,
        );
        props.onUpdated?.(updatedSigner);
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.SignerQuery.searchQueryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.ProjectSignerQuery.searchQueryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.SignerQuery.getQueryKey(props.signerId!),
        });
        await invalidateSignatureBlockQueries();
        onClose();
      },
      [props.signerId, signerQuery.data],
    ),
  );

  const onClose = () => {
    form.reset();
    form.clearErrors();
    setShowOverallErrors(false);
    props.onClose();
  };

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={onClose}
      isBlocking={true}
      title={i18n.t('title_edit')}
    >
      <Loading loading={form.formState.isSubmitting}>
        <form onSubmit={form.handleSubmitDefault}>
          <div className={styles.main}>
            <Field title={i18n.t('first_name_input')} className={styles.field}>
              <Input
                key={'firstName'}
                {...form.register('firstName', { ...requiredRule() })}
                errorText={form.formState.errors.firstName?.message}
              />
            </Field>
            <Field title={i18n.t('last_name_input')} className={styles.field}>
              <Input
                key={'lastName'}
                {...form.register('lastName', { ...requiredRule() })}
                errorText={form.formState.errors.lastName?.message}
              />
            </Field>
            <Field title={i18n.t('email_input')} className={styles.field}>
              <Input
                key={'email'}
                {...form.register('email', { ...requiredRule() })}
                errorText={form.formState.errors.email?.message}
              />
            </Field>
            <FormError>{showOverallErrors ? form.overallError : ''}</FormError>
          </div>
          <div className={styles.footer}>
            <Button
              className={styles.button}
              color={ButtonColor.Secondary}
              title={buttonTitle.t('cancel')}
              onClick={onClose}
            />
            <Button
              disabled={!form.formState.isValid}
              testId={'save-button'}
              className={styles.button}
              color={ButtonColor.Primary}
              type={'submit'}
              title={buttonTitle.t('save')}
            />
          </div>
        </form>
      </Loading>
    </CustomModal>
  );
};
